import { Form, Formik } from 'formik';
import { ModalStatusType } from '../../../types/common';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import InputText from '../../../components/input-text/inputText';
import CustomTextArea from '../../../components/text-area/textArea';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import {
  useGetActiveTransferAgent,
  useGetCurrentIssuerStockClasses,
  useGetPoolTypes,
  useGetSharePoolById,
  useGetVinylExternalReservePool,
  usePostSharePool,
  useWealthlaneAwardApi,
  useWealthlaneCapTableApi,
} from '../../../hooks';
import _ from 'lodash';
import { CommaFormatted } from '../../../utils/global';
import { SHARE_POOL_VALIDATION } from '../../../utils/validations/validations';
import FormSelect from '../../../components/dropdown/formSelect';
import CustomCheckBox from '../../../components/radio-button/customCheckBox';
import Heading from '../../../components/typography';
import React, { useEffect } from 'react';

interface PerformanceRuleType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  rowData?: any;
}

const SharePoolModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  setModalStatus,
  rowData,
}: PerformanceRuleType): JSX.Element => {
  const { handleRequest } = useWealthlaneAwardApi();
  const { handleRequest: capTableHandleRequest } = useWealthlaneCapTableApi();
  const { mutate, isLoading } = usePostSharePool(handleRequest, closeModal, rowData?.id);
  const handleSubmit = (values: any): void => {
    const payload = {
      ...values,
      numberOfSharesAuthorised: Number(values?.numberOfSharesAuthorised.replaceAll(',', '')),
      numberOfSharesUnAvailable: Number(values?.numberOfSharesUnAvailable.replaceAll(',', '')),
      numberOfSharesAvailable:
        Number(values?.numberOfSharesAuthorised.replaceAll(',', '')) -
        Number(values?.numberOfSharesUnAvailable.replaceAll(',', '')),
    };
    mutate(payload);
  };

  const { data: sharePoolById } = useGetSharePoolById(handleRequest, rowData?.id);
  const initialValue = {
    ...sharePoolById,
    numberOfSharesAuthorised: CommaFormatted(sharePoolById?.numberOfSharesAuthorised ?? ''),
    numberOfSharesUnAvailable: CommaFormatted(sharePoolById?.numberOfSharesUnAvailable ?? ''),
    numberOfSharesAvailable: CommaFormatted(sharePoolById?.numberOfSharesAvailable ?? ''),
  };
  const { data: activeTransferAgent } = useGetActiveTransferAgent(handleRequest);
  const { data: poolTypes, isLoading: isPoolTypesLoading } = useGetPoolTypes(handleRequest);

  const { data: vinylExternalReservePool, isLoading: isVinylExternalReservePoolLoading } =
    useGetVinylExternalReservePool(handleRequest, activeTransferAgent);

  const { data: stockClassListData, isLoading: isStockClassLoading } =
    useGetCurrentIssuerStockClasses(capTableHandleRequest, new Date().toISOString().split('T')[0]);

  const stockClassOptions = React.useMemo(
    () =>
      stockClassListData?.map((item) => ({
        label: item.name as string,
        value: item.id as string,
      })) ?? [],
    [stockClassListData]
  );

  useEffect(() => {}, [activeTransferAgent]);

  return (
    <Formik
      enableReinitialize
      initialValues={
        initialValue && modalStatus !== 'Add'
          ? initialValue
          : {
              ...initialData?.data,
              poolTypeId: poolTypes?.find((pt) => pt.value === 1) ? 1 : null,
            }
      }
      validationSchema={SHARE_POOL_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({
        touched,
        handleChange,
        errors,
        setFieldValue,
        setFieldTouched,
        values,
        setTouched,
      }: any) => {
        return (
          <>
            <ModalComponent
              id='sharePoolModal'
              show={isModalOpen}
              closeModal={closeModal}
              loading={false}
              size='lg'
              valueEdited={_.isEqual(
                values,
                modalStatus !== 'Add' ? initialValue : initialData?.data
              )}
              customAlertMessage={'Your changes will not be saved. Are you sure you want to close?'}
              title={`${modalStatus} Share Pool`}
              children={
                <Form id='sharePoolForm'>
                  <ModalBodyComponent id='sharePoolModalBody'>
                    <>
                      <div
                        id='sharePoolFormContainer'
                        className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-5'>
                        <div
                          id='poolNameContainer'
                          className='col-span-12 sm:col-span-2 lg:col-span-8'>
                          <InputText
                            id='poolName'
                            type='text'
                            label='Pool Name'
                            name='poolName'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            inputType={modalStatus}
                            disabled={modalStatus === 'View'}
                            required
                          />
                        </div>
                        <div
                          id='poolTypeIdContainer'
                          className='col-span-12 sm:col-span-2 lg:col-span-4'>
                          <FormSelect
                            id='poolTypeId'
                            type='text'
                            label='Pool Type'
                            name='poolTypeId'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                            options={poolTypes}
                            isLoading={isPoolTypesLoading}
                            values={values}
                            setFieldValue={setFieldValue}
                            inputType={modalStatus}
                            disabled={modalStatus === 'View'}
                            required
                          />
                        </div>
                      </div>
                      <div
                        id='sharePoolFieldsContainer'
                        className='grid sm:grid-cols-2 lg:grid-cols-3 gap-x-[14px]  '>
                        <InputText
                          id='numberOfSharesAuthorised'
                          type='text'
                          label='No. of Shares Authorized'
                          name='numberOfSharesAuthorised'
                          onChange={handleChange}
                          onFocus={(e: any) => {
                            values?.numberOfSharesAuthorised.replaceAll(',', '');
                          }}
                          onBlur={(e: any) => {
                            CommaFormatted(
                              e.target.value,
                              `numberOfSharesAuthorised`,
                              setFieldValue
                            );
                            setFieldTouched(`numberOfSharesAuthorised`, true);
                          }}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                        <InputText
                          id='numberOfSharesUnAvailable'
                          type='text'
                          label='No. of Shares Unavailable'
                          name='numberOfSharesUnAvailable'
                          onChange={handleChange}
                          onFocus={(e: any) => {
                            values?.numberOfSharesUnAvailable.replaceAll(',', '');
                          }}
                          onBlur={(e: any) => {
                            CommaFormatted(
                              e.target.value,
                              `numberOfSharesUnAvailable`,
                              setFieldValue
                            );
                            setFieldTouched(`numberOfSharesUnAvailable`, true);
                          }}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                        <InputText
                          id='numberOfSharesAvailable'
                          type='text'
                          label='No. of Shares Available'
                          name='numberOfSharesAvailable'
                          value={
                            modalStatus === 'Add' || modalStatus === 'Edit'
                              ? (() => {
                                  const authorised =
                                    values?.numberOfSharesAuthorised?.replace(/,/g, '') || '0';
                                  const unavailable =
                                    values?.numberOfSharesUnAvailable?.replace(/,/g, '') || '0';

                                  const authorisedNum = Number(authorised);
                                  const unavailableNum = Number(unavailable);

                                  // Check if both values are valid numbers
                                  if (isNaN(authorisedNum) || isNaN(unavailableNum)) {
                                    return '';
                                  }

                                  const result = authorisedNum - unavailableNum;
                                  return result >= 0 ? CommaFormatted(String(result)) : '';
                                })()
                              : ''
                          }
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={true}
                          required
                        />
                        <InputText
                          type='text'
                          label='Eligible Share Class'
                          name='eligibleShareClass'
                          id='eligibleShareClass'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          required
                        />
                        <FormSelect
                          id='stockClassId'
                          type='text'
                          label='Stock Class'
                          name='stockClassId'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          options={stockClassOptions}
                          isLoading={isStockClassLoading}
                          values={values}
                          setFieldValue={setFieldValue}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          placeholder='Select Stock Class'
                          isClearable
                        />
                        <div id='descriptionContainer' className='sm:col-span-3'>
                          <CustomTextArea
                            id='description'
                            label='Description'
                            name='description'
                            onChange={handleChange}
                            errors={errors}
                            inputType={modalStatus}
                            disabled={modalStatus === 'View'}
                            touched={touched}
                            rows='5'
                          />
                        </div>
                        {activeTransferAgent?.isActive && (
                          <div id='externalPoolReferenceContainer' className='sm:col-span-3'>
                            <CustomCheckBox
                              id='hasExternalPoolRefrence'
                              name='hasExternalPoolRefrence'
                              onChange={(e: any) => {
                                handleChange(e);
                                e.target.checked === false &&
                                  setFieldValue('externalPoolReferenceId', '');
                              }}
                              errors={errors}
                              touched={touched}
                              className={'individual-check'}
                              labelClassName={'whitespace-normal'}
                              disabled={modalStatus === 'View'}
                              checked={values?.hasExternalPoolRefrence}
                              label={
                                <Heading
                                  variant='body-xs'
                                  className='mt-1'
                                  title={'Has External Pool Reference'}
                                />
                              }
                              required
                            />
                          </div>
                        )}
                        {values?.hasExternalPoolRefrence && (
                          <div id='externalPoolNameContainer' className='sm:col-span-2 mt-4'>
                            <FormSelect
                              id='externalPoolReferenceId'
                              type='text'
                              label='External Pool Name'
                              name='externalPoolReferenceId'
                              viewName='externalPoolReferenceId'
                              onChange={handleChange}
                              errors={errors}
                              touched={touched}
                              options={vinylExternalReservePool}
                              isLoading={isVinylExternalReservePoolLoading}
                              values={values}
                              setFieldValue={setFieldValue}
                              inputType={modalStatus}
                              disabled={modalStatus === 'View'}
                              required
                            />
                          </div>
                        )}
                      </div>
                    </>
                  </ModalBodyComponent>
                  <ModalFooterComponent
                    id='sharePoolModalFooter'
                    modalStatus={modalStatus}
                    loading={isLoading}
                    isEditShow={false}
                    isTmc={
                      modalStatus === 'View' &&
                      rowData?.id &&
                      rowData?.approvalStatus !== 'Rejected'
                        ? true
                        : false
                    }
                    onCloseModal={() => {
                      closeModal();
                    }}
                    valueChanged={_.isEqual(values, initialValue || initialData?.data)}
                    onEditModal={() => {
                      setModalStatus('Edit');
                    }}
                    customAlertMessage={
                      'Your changes will not be saved. Are you sure you want to close?'
                    }
                  />
                </Form>
              }
            />
          </>
        );
      }}
    </Formik>
  );
};

export default SharePoolModal;
